import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown, Form, Message, Segment, Statistic, Table } from 'semantic-ui-react'
import { LoaderBar } from 'components'
import { VoiceService } from 'utility/services/voice'
import {
    formatRate,
    getVoiceApiErrorMessage,
    isVoiceLogNotInstalled,
    normalizeVoiceAccuracyReport,
    voiceAccuracyPeriodOptions,
} from './voice-utils'

const formatDate = value => {
    if (!value) return '-'
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString()
}

const RateTable = ({ title, rows, firstColumn, showLastUsed }) => (
    <>
        <h4>{title}</h4>
        <Table celled compact stackable>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>{firstColumn}</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Sessions</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Answered</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Misheard</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Undo</Table.HeaderCell>
                    {showLastUsed && <Table.HeaderCell>Last used</Table.HeaderCell>}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {rows.map(r => (
                    <Table.Row key={r.key}>
                        <Table.Cell>{r.name}</Table.Cell>
                        <Table.Cell textAlign="right">{r.sessions}</Table.Cell>
                        <Table.Cell textAlign="right">{r.answered}</Table.Cell>
                        <Table.Cell textAlign="right">
                            {formatRate(r.misheardRate)} ({r.misheard})
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                            {formatRate(r.undoRate)} ({r.undos})
                        </Table.Cell>
                        {showLastUsed && <Table.Cell>{formatDate(r.lastUsedOnUtc)}</Table.Cell>}
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    </>
)

/**
 * Settings > Voice inspection > Voice accuracy (company admins). The phone sends
 * counts only after each voice session (no recordings, no transcripts); this shows
 * how often it misheard answers, per inspector, template, question and language.
 */
export const VoiceAccuracyReport = () => {
    const [days, setDays] = useState(30)
    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(true)
    const [notInstalled, setNotInstalled] = useState(false)
    const [error, setError] = useState('')
    // Only the latest request may fill the card (switching 7 -> 90 days quickly).
    const requestRef = useRef(0)

    const load = useCallback(async () => {
        requestRef.current += 1
        const request = requestRef.current
        setLoading(true)
        setError('')
        setNotInstalled(false)
        try {
            const res = await VoiceService.getAccuracyReport(days)
            if (request !== requestRef.current) return
            setReport(normalizeVoiceAccuracyReport(res))
        } catch (err) {
            if (request !== requestRef.current) return
            setReport(null)
            if (isVoiceLogNotInstalled(err)) setNotInstalled(true)
            else setError(getVoiceApiErrorMessage(err))
        } finally {
            if (request === requestRef.current) setLoading(false)
        }
    }, [days])

    useEffect(() => {
        load()
    }, [load])

    const totals = report?.totals
    return (
        <Segment raised>
            <h3 style={{ marginTop: 0 }}>Voice accuracy</h3>
            <p style={{ color: '#666', maxWidth: 760 }}>
                How well the phone understood spoken answers. After each voice session
                the app sends counts only (no recordings, no transcripts). A high
                misheard or undo rate points to questions or options that are hard to
                say or to recognise.
            </p>
            <Form>
                <Form.Group>
                    <Form.Field width={4}>
                        <label>Period</label>
                        <Dropdown
                            selection
                            options={voiceAccuracyPeriodOptions()}
                            value={days}
                            onChange={(_, d) => setDays(Number(d.value) || 30)}
                        />
                    </Form.Field>
                </Form.Group>
            </Form>
            {loading && <LoaderBar content="Loading voice accuracy…" />}
            {!loading && notInstalled && (
                <Message info>Voice accuracy is not installed on this server yet.</Message>
            )}
            {!loading && error && (
                <Message negative>
                    {error}{' '}
                    <button type="button" className="ui mini basic button" onClick={load}>
                        Try again
                    </button>
                </Message>
            )}
            {!loading && totals && totals.sessions === 0 && (
                <Message info>No voice sessions yet.</Message>
            )}
            {!loading && totals && totals.sessions > 0 && (
                <>
                    <Statistic.Group size="tiny" widths={4}>
                        <Statistic>
                            <Statistic.Value>{totals.sessions}</Statistic.Value>
                            <Statistic.Label>Sessions</Statistic.Label>
                        </Statistic>
                        <Statistic>
                            <Statistic.Value>{totals.answered}</Statistic.Value>
                            <Statistic.Label>Answered by voice</Statistic.Label>
                        </Statistic>
                        <Statistic>
                            <Statistic.Value>{formatRate(totals.misheardRate)}</Statistic.Value>
                            <Statistic.Label>Misheard</Statistic.Label>
                        </Statistic>
                        <Statistic>
                            <Statistic.Value>{formatRate(totals.undoRate)}</Statistic.Value>
                            <Statistic.Label>Undone</Statistic.Label>
                        </Statistic>
                    </Statistic.Group>
                    <p style={{ color: '#777', marginTop: 12 }}>
                        {totals.users} inspector{totals.users === 1 ? '' : 's'} ·{' '}
                        {totals.questionsRead} questions read · {totals.instantAnswers}{' '}
                        answered while the question was read · {totals.confirms}{' '}
                        confirmations asked
                        {totals.readThenListenSessions > 0
                            ? ` · ${totals.readThenListenSessions} session(s) could not listen while reading`
                            : ''}
                    </p>

                    {report.byUser.length > 0 && (
                        <RateTable title="By inspector" firstColumn="Inspector" rows={report.byUser} showLastUsed />
                    )}
                    {report.byTemplate.length > 0 && (
                        <RateTable title="By template" firstColumn="Template" rows={report.byTemplate} />
                    )}

                    <h4>Questions to reword</h4>
                    <p style={{ color: '#777' }}>
                        Questions the phone often mishears - consider shorter wording or
                        clearer options.
                    </p>
                    {report.questions.length === 0 ? (
                        <Message info size="small">No question was misheard in this period.</Message>
                    ) : (
                        <Table celled compact stackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Question</Table.HeaderCell>
                                    <Table.HeaderCell>Template</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Sessions</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Misheard</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Confirmations</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Undo</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {report.questions.map(q => (
                                    <Table.Row key={q.key}>
                                        <Table.Cell>{q.text}</Table.Cell>
                                        <Table.Cell>{q.templateName}</Table.Cell>
                                        <Table.Cell textAlign="right">{q.sessions}</Table.Cell>
                                        <Table.Cell textAlign="right">{q.misses}</Table.Cell>
                                        <Table.Cell textAlign="right">{q.confirms}</Table.Cell>
                                        <Table.Cell textAlign="right">{q.undos}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}

                    {report.languages.length > 0 && (
                        <>
                            <h4>Languages</h4>
                            <Table celled compact collapsing>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Language</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">Sessions</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">Misheard</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {report.languages.map(l => (
                                        <Table.Row key={l.key}>
                                            <Table.Cell>{l.language}</Table.Cell>
                                            <Table.Cell textAlign="right">{l.sessions}</Table.Cell>
                                            <Table.Cell textAlign="right">{formatRate(l.misheardRate)}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </>
                    )}
                </>
            )}
        </Segment>
    )
}
